import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import api from '@dashboard/services/axiosApi'
import type { Partner, IntegrationVersion } from '@dashboard/types/partner'
import type { PlanTier } from '@dashboard/stores/subscriptionStore'
import { swapOutEspPlaceholder } from '@dashboard/modules/integration/services/integrationUtilities'
import { AnalyticsEventTypes, recordAnalyticsEvent } from '@dashboard/services/analytics'
import { useConfigStore } from '@dashboard/stores/configStore'
import { klona } from 'klona'
import { storeToRefs } from 'pinia'
import { unref, watch, watchEffect } from 'vue'

const partnerLogoOverrides: Record<string, string> = {
  beehiiv: '/img/onboarding/beehiiv.png',
  bento: '/img/onboarding/bento.png',
  encharge: '/img/onboarding/encharge.png',
  userlist: '/img/onboarding/userlist.png',
  gohighlevel: '/img/onboarding/gohighlevel.png',
  klaviyo: '/img/onboarding/klaviyo.svg',
  mailerlite: '/img/onboarding/mailerlite.png',
  pardot: '/img/onboarding/pardot.png'
}

/** Listing fields `GET /api/{pid}/unified-partners` adds to each provider. */
export type IntegrationPartner = Partner & {
  /** The project's plan doesn't include this provider (never true for a connected one). */
  locked?: boolean
  /** Lowest plan tier that includes this provider; null when every plan does. */
  required_tier?: PlanTier | null
  /** When the current connection was created (ISO 8601). */
  created_at?: string | null
}

/**
 * Helper to determine if a partner should use V2 endpoints
 */
const shouldUseV2Endpoints = (version: IntegrationVersion | undefined): boolean =>
  version === 'v2' || version === 'prefer_v2'

export const useIntegrationStore = defineStore('integration', () => {
  const partners = ref<IntegrationPartner[]>([])
  // Timestamp set when a brand-new integration is connected (not an update).
  // Used by SegmentGroupSync to distinguish "async initial load" from "user just connected."
  const newlyConnectedAt = ref<number | null>(null)
  const configStore = useConfigStore()
  const { currentTeamPid } = storeToRefs(configStore)

  const queryClient = useQueryClient()

  const transformPartners = (partners: IntegrationPartner[]) =>
    partners.map((partner) => ({
      ...partner,
      logo: partnerLogoOverrides[partner.name] || `/img/onboarding/${partner.name}.svg`
    }))

  // Use unified endpoint that returns all providers with integrationVersion
  const partnerEndpoint = computed(() => `/api/${currentTeamPid.value}/unified-partners`)

  /**
   * Fetches and sets the list of partners using Tanstack Query
   */
  const partnersQuery = useQuery({
    queryKey: computed(() => ['partners', currentTeamPid.value]),
    queryFn: () => api.get(partnerEndpoint.value).then((response) => transformPartners(response.data.partners)),
    enabled: computed(() => !!currentTeamPid.value)
  })

  watchEffect(() => {
    if (partnersQuery.data.value) {
      partners.value = klona(unref(partnersQuery.data))
    }
  })

  // Force refetch when team changes
  watch([currentTeamPid], () => {
    if (currentTeamPid.value) {
      void partnersQuery.refetch()
    }
  })

  /**
   * Save integration - routes to V1 or V2 based on partner.integrationVersion
   */
  const saveIntegrationMutation = useMutation({
    mutationFn: (variables: { partner: Partner; data: object }) => {
      // Legacy-only rows cannot sync; re-saving must create the native connection.
      const useV2 = variables.partner.needsReconnect || shouldUseV2Endpoints(variables.partner.integrationVersion)

      if (useV2) {
        return api.post(`/api/${currentTeamPid.value}/connections/${variables.partner.name}`, {
          ...variables.data
        })
      }

      return api.post(`/api/${currentTeamPid.value}/integrations/${variables.partner.name}`, {
        ...variables.data
      })
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['partners', currentTeamPid.value]
      })

      const isNew = !variables.partner.hasExistingIntegration
      void recordAnalyticsEvent(
        isNew ? AnalyticsEventTypes.INTEGRATION_CONNECTED : AnalyticsEventTypes.INTEGRATION_UPDATED,
        {
          partnerName: variables.partner.name,
          partnerLabel: variables.partner.label
        }
      )

      if (isNew) {
        newlyConnectedAt.value = Date.now()
      }
    }
  })

  const save = () => {
    const { mutateAsync, error, isPending } = saveIntegrationMutation
    return { mutateAsync, error, isPending }
    // saveIntegrationMutation.mutate({ partner, data })
  }

  /**
   * Remove integration - routes to V1 or V2 based on partner.integrationVersion
   */
  const removeIntegrationMutation = useMutation({
    mutationFn: (partner: Partner) => {
      // For removal, only use V2 endpoint if the integration IS v2
      // (not prefer_v2, which means no existing integration)
      const useV2 = partner.integrationVersion === 'v2'

      if (useV2) {
        return api.delete(`/api/${currentTeamPid.value}/connections/${partner.name}`)
      }

      return api.delete(`/api/${currentTeamPid.value}/integrations/${partner.name}`)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['partners', currentTeamPid.value]
      })
    }
  })

  const remove = async (partner: Partner) => {
    await removeIntegrationMutation.mutateAsync(partner)
    void recordAnalyticsEvent(AnalyticsEventTypes.INTEGRATION_DISCONNECTED, {
      partnerName: partner.name,
      partnerLabel: partner.label
    })
  }

  /**
   * Getter for the integrated partner
   */
  const integratedPartner = computed(() => partners.value.find((partner) => partner.hasExistingIntegration === true))

  watch(
    integratedPartner,
    (newVal) => {
      if (!newVal) {
        return
      }
      swapOutEspPlaceholder(newVal)
    },
    { immediate: true }
  )

  const integratedPartnerName = computed(() => integratedPartner.value?.name)

  return {
    partners,
    integratedPartner,
    integratedPartnerName,
    newlyConnectedAt,
    isLoading: partnersQuery.isLoading,
    isError: partnersQuery.isError,
    refetch: partnersQuery.refetch,
    save,
    remove
  }
})
